/**
 * One fetch wrapper. Same origin in production (the Worker serves this SPA), proxied in dev.
 *
 * The session cookie rides along on its own (`credentials: "include"`); nothing here holds
 * a code or a token. A 401 from anywhere means the cookie ran out, so the gate in App.tsx
 * registers `onUnauthorized` and takes over the screen - otherwise an expired session would
 * show up as a red error string on whatever page happened to be open. `/v1/auth/*` is
 * exempt: a wrong code at the login screen is that screen's business.
 */

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly body: unknown = null) {
    super(message);
  }
}

let unauthorized: (() => void) | null = null;

/** What to do when the session has gone. Registered once, by the gate. */
export function onUnauthorized(handler: () => void): void {
  unauthorized = handler;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const json = (await response.json().catch(() => null)) as { error?: string } | null;
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith("/v1/auth/")) unauthorized?.();
    throw new ApiError(response.status, json?.error ?? `Request failed (${response.status})`, json);
  }
  return json as T;
}

export const api = <T,>(path: string) => request<T>("GET", path);
export const post = <T,>(path: string, body: unknown = {}) => request<T>("POST", path, body);
export const patch = <T,>(path: string, body: unknown) => request<T>("PATCH", path, body);
// A 204 has no body; `request` reads that as null rather than failing to parse it.
export const del = (path: string) => request<null>("DELETE", path);

/**
 * A file rather than a document: one call's recording, fetched with the session cookie.
 *
 * FETCHED RATHER THAN POINTED AT WITH `src`. The playback route is behind the console gate
 * and answers 404 for a call that is not this demo's, and a bare `src` on an `<audio>` gives
 * neither `credentials: "include"` on the split-origin dev setup nor any way to say what went
 * wrong. The caller turns these bytes into an object URL and revokes it when it is done.
 */
export async function blob(path: string): Promise<Blob> {
  const response = await fetch(path, { credentials: "include" });
  if (!response.ok) {
    if (response.status === 401) unauthorized?.();
    throw new ApiError(response.status, `Could not load the recording (${response.status})`);
  }
  return await response.blob();
}
